import { useState, useMemo } from 'react';
import { ChevronDown, Search, MessageCircle, Info } from 'lucide-react';

const faqData = [
  {
    category: "Myths",
    question: "Does extraction of teeth affect your eyesight?",
    answer:
      "There is no connection between extraction of teeth and eye sight. When the upper teeth decay (due to tooth caries, Periodontitis, etc.), the pain extends up to the eyes. Hence people have this misconception. There is no truth in it. It is essential that decayed teeth have to be removed because they might spread infection into other parts of body.",
  },
  {
    category: "General",
    question: "Do teeth move apart, if you get youth teeth cleaned your eye sight ?",
    answer:
      "Due to plaque and calculus, the gums and bone around the teeth recede and gaps are caused between the teeth. However, as the calculus covers those gaps, they are not visible. When the dentist cleans the teeth and removes the Calculus, the gaps among the teeth become visible. This gives the impression that teeth move apart due to cleaning or scaling. This is not true. If one does not get one’s teeth cleaned and the Calculus remains for longer time, it would affect the neighboring healthy teeth and they would fall.",
  },
  {
    category: "Myths",
    question: "Do supernumerary teeth (tooth over tooth) make you lucky ?",
    answer:
      "Due to supernumerary teeth, forget about luck, cleaning the teeth would be problematic as the brush does not reach there. Thus one has the problems of plaque, gum-disease and cavities. In addition, one has bad breath and movement of teeth. When one has supernumerary teeth, one must consult a dentist and get orthodontic treatment done.",
  },
  {
    category: "General",
    question: "Do gaps between teeth make your smile unimpressive?",
    answer:
      "The Nicotine and Tar in cigarettes accumulate as plaque on the teeth of smokers and once bacteria settles in the plaque, severe dental problems follow. Similarly, in the case of tobacco chewers and those who consume pan masala etc., the lime and other hazardous ingredients damage the sensitive membrane in the mouth and create oral sub-mucus (fibrosis). As a consequence, the tender oral skin gets hardened. This enhances the possibility of acquiring oral cancer by 3%. These people have multiplied possibility of gum diseases and oral infections. Hence one should quit smoking and chewing tobacco products immediately.",
  },
  {
    category: "Habits",
    question: "Do cigarettes and pan masalas spoil ones teeth?",
    answer:
      "The purpose of brushing is only to remove the food debris on the surface of the teeth. Brushing for longer duration leads only to worn teeth but does not help brightening teeth. The ideal brushing time is in between 2 to 3 minutes. If we brush in the morning and after every meal and before hitting the bed, the food particles would not stick in the mouth. Hence, plaque would not form and colour of the teeth remains intact.",
  },
  {
    category: "Hygiene",
    question: "Do we have to brush longer to get our teeth whiter?",
    answer:
      "As one does not brush ones teeth properly, plaque forms on the teeth and they change colour. Frequent consumption of tea/coffee and artificially coloured dishes changes the colour of the teeth. Smoking and chewing pan masala and other tobacco products changes the look of the teeth. However, these stains can be removed by ordinary scaling and polishing of the teeth. In some cases, due to fluorine contamination of water, intrinsic stains formed in the deeper layers of the teeth. Still, by the method of bleaching-bonding-cleaning the deeply stained teeth can be made look good.",
  },
  {
    category: "Hygiene",
    question: "Should we avoid sweets to prevent cavities?",
    answer:
      "Excessive consumption of Sweets usually leads to cavities in the teeth. Children eat sticky chocolates and do not brush the whole day. So, bacteria spread in the sticky particles of chocolate and lead to caries. We should cultivate among the children the habit of gargling and brushing after eating such sticky confectionaries. We better avoid eating sweets before going to bed and if we do, we should invariably brush our teeth immediately.",
  },
  {
    category: "General",
    question: "Is the problem of dental caries hereditary?",
    answer:
      "No, caries is not a hereditary problem. But, it was observed that as children emulate the dental care practices of the adults, the dental health of the whole family depends on its dental care practices. Those who neglect dental care are more susceptible to dental problems. But there is no possibility of acquiring dental problems hereditarily.",
  },
  {
    category: "Emergency",
    question: "If we lose a tooth in an accident, can it be restored?",
    answer:
      "If one loses a tooth in an accident, one has to preserve the tooth in milk or salt water or beneath the tongue. If one can reach an expert dentist within an hour, the tooth can be restored by way splinting. Root canal treatment has to be done to that tooth. If we restore such lost tooth within one hour, the success rate is 90%. Usually children between the ages of 7 to 11 lose teeth while playing. So, it is better that children are provided with mouth guards while playing.",
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Filter logic
  const filteredFaqs = useMemo(() => {
    return faqData.filter(
      (item) =>
        item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.answer.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);

  return (
    <div className="min-h-screen bg-[#f8fafc] py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        
        {/* Header Section */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 mb-4 text-sm font-semibold tracking-wider text-blue-700 uppercase bg-blue-50 rounded-full">
            Help Center
          </span>
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4">
            Patient Education <span className="text-blue-600">& FAQ</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            We believe an informed patient is a healthy patient. Explore common dental myths, facts, and hygiene tips.
          </p>
        </div>

        {/* Search Bar */}
        <div className="relative mb-10">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-slate-400" />
          </div>
          <input
            type="text"
            placeholder="Search your question (e.g., 'whitening', 'myths')..."
            className="block w-full pl-11 pr-4 py-4 bg-white border border-slate-200 rounded-2xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none text-slate-700"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {filteredFaqs.length > 0 ? (
            filteredFaqs.map((item, index) => (
              <div
                key={index}
                className={`group bg-white rounded-2xl border transition-all duration-300 ${
                  openIndex === index 
                    ? 'border-blue-200 shadow-md ring-1 ring-blue-50' 
                    : 'border-slate-200 hover:border-blue-300 shadow-sm'
                }`}
              >
                <button
                  onClick={() => toggleAccordion(index)}
                  className="w-full px-6 py-5 text-left flex justify-between items-start gap-4 focus:outline-none"
                >
                  <div className="flex flex-col gap-2">
                    <span className={`text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded w-fit ${
                      item.category === 'Myths' ? 'bg-amber-50 text-amber-700' : 
                      item.category === 'Emergency' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'
                    }`}>
                      {item.category}
                    </span>
                    <h3 className={`text-lg font-bold leading-tight transition-colors ${
                      openIndex === index ? 'text-blue-700' : 'text-slate-800'
                    }`}>
                      {item.question}
                    </h3>
                  </div>
                  <div className={`mt-6 transition-transform duration-300 flex-shrink-0 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}>
                    <ChevronDown className={`w-6 h-6 ${openIndex === index ? 'text-blue-600' : 'text-slate-400'}`} />
                  </div>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    openIndex === index ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 pb-6 pt-2">
                    <div className="h-px w-full bg-slate-100 mb-4" />
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <Info className="w-5 h-5 text-blue-500" />
                      </div>
                      <p className="text-slate-600 leading-relaxed text-base">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-slate-300">
              <p className="text-slate-500 text-lg">No matches found for "{searchTerm}"</p>
              <button 
                onClick={() => setSearchTerm("")}
                className="mt-2 text-blue-600 font-semibold hover:underline"
              >
                View all questions
              </button>
            </div>
          )}
        </div>

        {/* Refined Footer CTA */}
        <div className="mt-16 bg-blue-900 rounded-3xl p-8 text-center text-white shadow-xl">
          <div className="flex justify-center mb-4">
            <div className="bg-white/10 p-3 rounded-full">
              <MessageCircle className="w-8 h-8 text-blue-200" />
            </div>
          </div>
          <h3 className="text-2xl font-bold mb-2">Still have questions?</h3>
          <p className="text-blue-100 mb-6 opacity-90">
            Our expert dental team is here to provide personalized clarity on your concerns.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-block bg-blue-600 hover:bg-blue-500 text-white px-8 py-3 rounded-xl font-bold transition-all shadow-lg active:scale-95"
            >
              Contact Us Now
            </a>
            <a
              href="tel:+9490948081"
              className="inline-block bg-white/10 hover:bg-white/20 text-white px-8 py-3 rounded-xl font-bold transition-all border border-white/20"
            >
              Call Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}